class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );

      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    // If input is empty, completely clear the search
    if (!searchTerm.length) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    // Do nothing when input is empty
    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;

      case 'ArrowDown':
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
    }

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    this.setLiveRegionLoadingState();

    // Use cached result
    if (this.cachedResults[queryKey]) {
      // Make sure user still has the same search term
      if (this.getQuery().toLowerCase() !== searchTerm.toLowerCase()) {
        return;
      }

      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // fetch(
    //   `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent(
    //     'resources[type]'
    //   )}=product&${encodeURIComponent(
    //     'resources[limit]'
    //   )}=4&section_id=predictive-search`,
    //   {
    //     signal: this.abortController.signal
    //   }
    // )
    const trimmedSearchTerm = searchTerm.trim();
    const queryTerm = trimmedSearchTerm.endsWith('*') ? trimmedSearchTerm : `${trimmedSearchTerm}*`;
    // const queryTerm = searchTerm.trim();
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(queryTerm)}` +
      `&resources[type]=product` +
      `&resources[limit]=5` +
      `&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag` +
      `&resources[options][unavailable_products]=last` +
      `&section_id=predictive-search`,
      {
        signal: this.abortController.signal
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        // Check if the input is still the same
        const currentSearchTerm = this.getQuery();

        if (
          !currentSearchTerm.length ||
          currentSearchTerm.toLowerCase() !== searchTerm.toLowerCase()
        ) {
          return;
        }

        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;

        // Double-check before rendering
        if (
          this.getQuery().toLowerCase() === searchTerm.toLowerCase()
        ) {
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch((error) => {
        // Ignore aborted requests
        if (error.name === 'AbortError') {
          return;
        }

        this.close();
        console.error('Predictive search error:', error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector('.predictive-search-status');

    this.loadingText =
      this.loadingText ||
      this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    // Don't render results if search input is empty
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const liveRegionCount = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (liveRegionCount) {
      this.setLiveRegionText(liveRegionCount.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.getElementById(
      'shopify-section-header'
    );

    if (!header) {
      return window.innerHeight;
    }

    this.resultsMaxHeight =
      window.innerHeight -
      header.getBoundingClientRect().bottom;

    return this.resultsMaxHeight;
  }

  open() {
    // Never open with an empty search
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight ||
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');

      // IMPORTANT:
      // Remove old predictive search results
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }

      // Abort any pending request
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', false);
    }

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);

    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);